<template>
  <div class="register-tab">
    <SalesRegisterToolbar
        ref="toolbarRef"
        popover-width="400px"
        :filters="appliedFilters"
        :chip-meta="filterChipMeta"
        @go="onClickGo"
        @open-filter="onOpenFilter"
        @apply="onApplyFilters"
        @clear="onClearFilters"
        @remove-chip="onRemoveChip"
        @print="onClickToPrint"
        @export="exportTable"
    >
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-between">
          <label class="filter-label">Party</label>
          <span class="filter-search-icon" @click="onOpenContactSearchModal">
            <i class="fas fa-search"></i>
          </span>
        </div>
        <AsyncSelect
            placeholder="Select Party"
            v-model="draftFilters.party"
            :api-service="fetchContactProfiles"
            :additional-query="additionalContactOption"
            :format-label="formatPatientLabel"
        />
      </div>
      <div class="col-12">
        <label class="filter-label">Product/Service</label>
        <AsyncSelect
            placeholder="Select Product/Service"
            :api-service="fetchProductList"
            v-model="draftFilters.product"
            label="text"
            :format-label="option => option.text"
        />
      </div>
    </SalesRegisterToolbar>
    <ListTable
        :tableItems="investigations"
        :currentPage="customerData && customerData.data && customerData.data.current_page"
        :lastPage="customerData && customerData.data && customerData.data.last_page"
        :perPage="customerData && customerData.data && customerData.data.per_page"
        :totalValue="customerData && customerData.total"
        :isLoaded="hasLoaded"
    />
    <div class="mt-2">
      <Pagination :offset="offset" @onPageChange="onPageChange"/>
    </div>
    <ContactDropdownFilter
        v-if="$store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        type="customer"
    />
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Pagination from '@/components/atom/Pagination'
import Loader from '@/components/atom/LoaderComponent.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import ListTable from '@/components/molecule/company/inventory/product-sales-register/CustomerTabTable.vue'
import SalesRegisterToolbar from '@/components/molecule/company/inventory/product-sales-register/SalesRegisterToolbar.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handlePurchase from '@/services/modules/purchase'
import handleHospitalReport from '@/services/modules/hospital/report'
import handleContact from '@/services/modules/contact'
import handleCompany from '@/services/modules/company'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import itemPrinter from '@/services/utils/pdf/saleRegisterItem'

const $router = useRouter()
const $route = useRoute()
const $store = useStore()
const showError = inject('showError')

const { fetchInvestigationInvoiceReport } = handleHospitalReport()
const { fetchProductList } = handlePurchase()
const { fetchContactProfiles } = handleContact()
const { fetchCompanyInfo } = handleCompany()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { exportToPDF } = itemPrinter()

const loading = ref(false)
const hasLoaded = ref(false)
const investigations = ref([])
const customerData = ref({})
const companyInfo = ref({})
const toolbarRef = ref(null)
const offset = 20

const additionalContactOption = {
  with_mobile: 1,
  dealer_id: '',
  roles: 'customer'
}

const filterChipMeta = [
  { key: 'party', label: 'Party', text_key: 'name' },
  { key: 'product', label: 'Product', text_key: 'text' }
]

const getEmptyFilters = () => {
  return {
    party: null,
    product: null
  }
}

const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())

const companyId = computed(() => $route.params.companyId)
const start = computed(() => $route.query.start)
const end = computed(() => $route.query.end)
const page = computed(() => $route.query.page)
const token = computed(() => localStorage.getItem('token'))

const getQuery = () => {
  const filters = appliedFilters.value
  let query = '?company_id=' + companyId.value
  query += '&group_by_customer=' + true
  if (filters.party) query += '&contact_profile_id=' + filters.party.id
  if (filters.product) query += '&product_id=' + filters.product.id
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (page.value) query += '&page=' + page.value
  query += '&offset=' + offset
  return query
}

const getPrintParams = () => {
  return {
    contact_profile: !! appliedFilters.value.party,
    product_id: !! appliedFilters.value.product
  }
}

const setPagination = (data) => {
  $store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

const resetTable = () => {
  investigations.value = []
  customerData.value = {}
  $store.commit('resetPagination')
}

const investigationInvoiceList = async () => {
  loading.value = true
  try {
    const res = await fetchInvestigationInvoiceReport(getQuery())
    if (! res.status) {
      resetTable()
      return
    }
    investigations.value = res.data.data
    customerData.value = res
    setPagination(res.data)
  } catch (err) {
    showError('Something went wrong')
  } finally {
    hasLoaded.value = true
    loading.value = false
  }
}

const resetPage = async () => {
  $store.commit('resetPagination')
  await $router.push({ path: $route.path, query: Object.assign({}, $route.query, { page: 1 }) })
}

const onClickGo = async () => {
  await resetPage()
  await investigationInvoiceList()
}

const onPageChange = async (page) => {
  const routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({ path: $route.path, query: routeQuery })
  await investigationInvoiceList()
}

const onOpenFilter = () => {
  draftFilters.value = Object.assign({}, appliedFilters.value)
}

const onApplyFilters = async () => {
  appliedFilters.value = Object.assign({}, draftFilters.value)
  await onClickGo()
}

const onClearFilters = async () => {
  appliedFilters.value = getEmptyFilters()
  draftFilters.value = getEmptyFilters()
  await onClickGo()
}

const onRemoveChip = async (key) => {
  appliedFilters.value[key] = getEmptyFilters()[key]
  await onClickGo()
}

const onOpenContactSearchModal = () => {
  $store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  const draft = Object.assign({}, draftFilters.value, { party: contact })
  if (toolbarRef.value) toolbarRef.value.openFilter()
  draftFilters.value = draft
}

const onClickToPrint = async () => {
  loading.value = true
  try {
    const res = await fetchInvestigationInvoiceReport(getQuery() + '&is_all=true')
    if (! res.status) {
      showError('No data found')
      return
    }
    exportToPDF(companyInfo.value, res, false, true, start.value || '', end.value || '', getPrintParams())
  } catch (err) {
    showError('Something went wrong')
  } finally {
    loading.value = false
  }
}

const exportTable = () => {
  const url = `${process.env.VUE_APP_BASE_URL}/export/reports/investigation-invoice-report${getQuery()}&_token=${token.value}&is_export=1&is_customer=1`
  const anchor = document.createElement('a')
  anchor.setAttribute('href', url)
  anchor.click()
}

const setCompanyInfo = async () => {
  try {
    const res = await fetchCompanyInfo(companyId.value)
    companyInfo.value = res.data
  } catch (err) {
    showError('Failed to load company info')
  }
}

onMounted(() => {
  setCompanyInfo()
})
</script>
